import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaEllipsisV, 
  FaEdit, 
  FaTrashAlt, 
  FaFileAlt, 
  FaEye 
} from 'react-icons/fa';

const ActionMenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const MenuButton = styled.button`
  background: var(--bg-light);
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-sm);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  
  &:hover {
    background: var(--gray-100);
    border-color: var(--primary-color);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(44, 62, 80, 0.2);
  }
  
  svg {
    color: var(--text-muted);
    font-size: var(--font-size-sm);
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--bg-light);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  min-width: 160px;
  padding: var(--spacing-xs) 0;
  margin-top: var(--spacing-xs);
  
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    right: 8px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid var(--bg-light);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -9px;
    right: 8px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid var(--gray-200);
  }
`;

const MenuItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: var(--spacing-sm) var(--spacing-lg);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  
  &:hover {
    background: var(--gray-100);
  }
  
  &.danger:hover {
    background: var(--danger-color);
    color: var(--text-light);
  }
  
  svg {
    font-size: var(--font-size-sm);
    width: 16px;
  }
`;

const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  text-decoration: none;
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--gray-100);
    color: var(--text-primary);
    text-decoration: none;
  }
  
  svg {
    font-size: var(--font-size-sm);
    width: 16px;
  }
`;

const ModernActionMenu = ({ 
  studyId, 
  onDelete, 
  onView, 
  studyName,
  apiUri 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleDelete = () => {
    onDelete(studyId, studyName);
    setIsOpen(false);
  };

  const handleView = () => {
    if (onView) {
      onView(studyId);
    }
    setIsOpen(false);
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Fechar menu quando clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.action-menu-container')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <ActionMenuContainer className="action-menu-container">
      <MenuButton onClick={toggleMenu} aria-label="Opções">
        <FaEllipsisV />
      </MenuButton>
      
      {isOpen && (
        <MenuDropdown>
          <MenuLink
            to={`${apiUri}study_pdf?ID_DM014=${studyId}`}
            target="_blank"
            onClick={() => setIsOpen(false)}
          >
            <FaFileAlt />
            Visualizar PDF
          </MenuLink>
          
          {onView && (
            <MenuItem onClick={handleView}>
              <FaEye />
              Visualizar
            </MenuItem>
          )}
          
          <MenuLink
            to={`./../edit/${studyId}`}
            onClick={() => setIsOpen(false)}
          >
            <FaEdit />
            Editar
          </MenuLink>
          
          <MenuItem 
            className="danger" 
            onClick={handleDelete}
          >
            <FaTrashAlt />
            Excluir
          </MenuItem>
        </MenuDropdown>
      )}
    </ActionMenuContainer>
  );
};

export default ModernActionMenu;
